import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from "@tiptap/core";
import Input from "../../../../../../components/Input/Input";
import Dropdown from "../../../../../../components/Dropdown/Dropdown";
import Button from "../../../../../../components/Button/Button";
import AttachmentInput, {
  type AttachmentInputRef,
  type AttachmentItem,
} from "../../../../../../components/Common/AttachmentInput";
import RichTextEditor from "../../../../../../components/RichTextEditor/RichTextEditor";
import RichTextEditorToolbar from "../../../../../../components/RichTextEditor/RichTextEditorToolbar";
import MultiSelectSearchableDropdown from "../../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import LeftArrowIcon from "../../../../../../assets/svg/left-arrow-icon";
import ImageIcon from "../../../../../../assets/svg/v1/image-icon";
import TicketCategorySelector from "./components/TicketCategorySelector";
import PrioritySelector from "./components/PrioritySelector";
import AddCustomFieldMenu from "./components/AddCustomFieldMenu";
import SystemAndIssueTypeFields from "./components/SystemAndIssueTypeFields";
import ScrollToFirstError from "./components/ScrollToFirstError";
import { CreateTicketDynamicFields } from "./components/CreateTicketDynamicFields";
import { DeleteHelpdeskCustomFieldModal } from "./components/DeleteHelpdeskCustomFieldModal";
import { HelpdeskTicketCustomFieldModal } from "../TicketDetails/components/TicketOverview/components/HelpdeskTicketCustomFieldModal";
import { createHandleAttachmentsChange } from "../../../../../Communications/utils/sendAttachmentFunctions";
import { visibleScrollbar } from "../../../../../../utils/functions/get-visible-scrollbar";
import { customToast } from "../../../../../../utils/custom-toast/custom-toast";
import {
  useGetHelpdeskCustomFieldsQuery,
  useGetHelpdeskTagsQuery,
  useCreateHelpdeskTicketMutation,
  useUpdateTicketMutation,
  useGetTicketQuery,
  useUploadHelpdeskAttachmentMutation,
  useDeleteHelpdeskCustomFieldMutation,
  useGetTicketAttachmentsQuery,
  useCreateTicketAttachmentMutation,
  useDeleteTicketAttachmentMutation,
  useCreateHelpdeskTagMutation,
  useGetTicketCategoriesQuery,
} from "../../../../../../redux/services/helpdeskApi";
import {
  buildTicketValidationSchema,
  TICKET_DESCRIPTION_TOOLBAR_SCHEMA,
  PRIORITY_NUMBER_TO_STRING,
  PRIORITY_STRING_TO_NUMBER,
  FEATURE_REQUESTS_SYSTEM_NAME,
  TICKET_ATTACHMENT_ACCEPT,
  TICKET_ATTACHMENT_FORMATS_LABEL,
} from "./utils/constants";
import type { CreateTicketFormValues, CustomFieldType } from "./utils/types";
import { isDescHtmlEmpty } from "./utils/functions";
import type { HelpdeskCustomFieldDefinition } from "../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import {
  orderCustomFieldsForRender,
  serializeTicketCustomFieldsPayload,
} from "../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import _ from "lodash";
import { useGetPropertiesListQuery } from "../../../../../../redux/services/playgroundApi";
import Header from "../../../../../../components/Header/Header";
import RequestSupportSelector from "./components/RequestSupportSelector";
import PulseLoader from "../../../../../../components/Loader/PulseLoader";
import UploadLeadsIcon from "../../../../../../assets/svg/upload-leads-icon";
import AddIcon from "../../../../../../assets/svg/add-icon";
import { MAX_IMAGE_SIZE_BYTES } from "../../../../../../components/RichTextEditor/utils/constants/constants";
import DueDateAlertModal from "../TicketDetails/components/TicketOverview/components/DueDateAlertModal";
import type { RootState } from "../../../../../../redux/store";
import { getClientCRMFlag } from "../../../../../../utils/constants/clientCustomCRMFlags";
import { getHelpdeskLabels } from "../../../../../../utils/constants/mappings";
import { PermissionDisabledWrapper } from "../PermissionDisabledWrapper";
import { useHasHelpdeskUpdateAccess } from "../../utils/helpdeskPermissions";

const DIRECT_ISSUE_CATEGORY_MAP: Record<string, string> = {
  sales: "Sales",
  marketing: "Marketing",
};

const SiteNameAutoSelect = ({
  isEditMode,
  options,
  currentValue,
  onAutoSelect,
}: {
  isEditMode: boolean;
  options: Array<{ value: string }> | undefined;
  currentValue: string;
  onAutoSelect: (value: string) => void;
}) => {
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  useEffect(() => {
    if (isEditMode) return;
    if (hasAutoSelected) return;
    if (currentValue) {
      setHasAutoSelected(true);
      return;
    }
    if (options?.length !== 1) return;
    setHasAutoSelected(true);
    onAutoSelect(options[0].value);
  }, [isEditMode, hasAutoSelected, currentValue, options, onAutoSelect]);

  return null;
};

const initialValues: CreateTicketFormValues = {
  ticketCategory: "sales",
  fullName: "",
  email: "",
  siteName: "",
  ticketSubject: "",
  systemName: "",
  issueType: "",
  priority: undefined,
  ticketDescription: "",
  custom_fields: {},
};

export const CreateTicket = () => {
  const { ticketId } = useParams<{ ticketId?: string }>();
  const isEditMode = Boolean(ticketId);

  const navigate = useNavigate();

  const helpdeskCategoryAccess = useSelector(
    (state: RootState) => state.auth.user?.helpdesk_category_access
  );
  const authCurrentClientCode = useSelector(
    (state: RootState) => state?.auth?.user?.current_client_code
  );
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region);
  const helpdeskStages = useSelector(
    (state: RootState) => state.helpdesk.stages
  );
  const [selectedTicketCategory, setSelectedTicketCategory] =
    useState<string>(isEditMode ? "" : "sales");

  const canUpdate = useHasHelpdeskUpdateAccess(
    selectedTicketCategory || undefined
  );

  const hasHelpdeskBackButtonControl = getClientCRMFlag(
    "hasHelpdeskBackButtonControl"
  );
  const hasTickets = helpdeskStages?.some((stage) => stage?.total > 0);
  const showBackButton =
    !hasHelpdeskBackButtonControl ||
    (hasHelpdeskBackButtonControl &&
      (!!helpdeskCategoryAccess?.length || !!hasTickets));

  const { data: ticketCategories } = useGetTicketCategoriesQuery();
  const isHelpdeskSupportCategory =
    ticketCategories?.find((c) => c.key === selectedTicketCategory)
      ?.display_name === "Helpdesk Support";

  const { data: customFieldsData } = useGetHelpdeskCustomFieldsQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const {
    data: helpdeskTagsData,
    refetch: refetchHelpdeskTags,
    isFetching: isFetchingHelpdeskTags,
  } = useGetHelpdeskTagsQuery({});
  const { data: ticketData, isLoading: isTicketLoading } = useGetTicketQuery(
    ticketId!,
    { skip: !ticketId }
  );
  const { data: ticketAttachmentsData } = useGetTicketAttachmentsQuery(
    ticketId!,
    { skip: !ticketId }
  );
  const { data: propertiesData } = useGetPropertiesListQuery(
    { managerId: authCurrentClientCode ?? "", page: 1, pageSize: 1000 },
    { skip: !authCurrentClientCode }
  );

  const [createTicket] = useCreateHelpdeskTicketMutation();
  const [updateTicket] = useUpdateTicketMutation();
  const [uploadAttachment] = useUploadHelpdeskAttachmentMutation();
  const [createTicketAttachment] = useCreateTicketAttachmentMutation();
  const [deleteTicketAttachment] = useDeleteTicketAttachmentMutation();
  const [deleteCustomField, { isLoading: isDeletingCustomField }] =
    useDeleteHelpdeskCustomFieldMutation();
  const [createHelpdeskTag, { isLoading: isLoadingCreateTagMutation }] =
    useCreateHelpdeskTagMutation();
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [requestSupport, setRequestSupport] = useState(false);
  const [isDescDragOver, setIsDescDragOver] = useState(false);
  const [customFieldModal, setCustomFieldModal] = useState<{
    open: boolean;
    fieldType: CustomFieldType | null;
    editingField?: HelpdeskCustomFieldDefinition | null;
  }>({ open: false, fieldType: null, editingField: null });
  const [fieldPendingDelete, setFieldPendingDelete] =
    useState<HelpdeskCustomFieldDefinition | null>(null);
  const [removedAttachmentKeys, setRemovedAttachmentKeys] = useState<
    Set<string>
  >(new Set());

  const [initialAttachmentKeys, setInitialAttachmentKeys] = useState<
    string[] | null
  >(null);

  const [showDueDateAlertModal, setShowDueDateAlertModal] = useState(false);
  const [pendingPriority, setPendingPriority] = useState<number | null>(null);

  const tagsRefetchRef = useRef<ReturnType<typeof refetchHelpdeskTags> | null>(
    null
  );
  const hasPrefilledEditor = useRef(false);
  const attachmentsRef = useRef(attachments);
  const attachmentRef = useRef<AttachmentInputRef>(null);
  const isUpdatingRef = useRef(false);
  const formScrollRef = useRef<HTMLDivElement>(null);
  const editorScrollRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const descContainerRef = useRef<HTMLDivElement>(null);
  const imageDescInputRef = useRef<HTMLInputElement>(null);
  const editAttachmentInputRef = useRef<HTMLInputElement>(null);

  editorRef.current = editor;

  const editInitialValues = useMemo<CreateTicketFormValues | null>(() => {
    if (!isEditMode || !ticketData) return null;
    const customFields = ticketData?.custom_fields ?? {};
    const systemName = (customFields["system-name"] as string) ?? "";
    const issueType = (customFields["issue-type"] as string) ?? "";

    const otherCf: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(customFields)) {
      if (k !== "system-name" && k !== "issue-type") {
        otherCf[k] = v;
      }
    }

    return {
      ticketCategory: ticketData?.ticket_category,
      fullName: ticketData?.name || "",
      email: ticketData?.email || "",
      siteName: ticketData?.property_name || "",
      ticketSubject: ticketData?.title || "",
      systemName,
      issueType,
      priority: PRIORITY_STRING_TO_NUMBER[ticketData?.priority] ?? undefined,
      ticketDescription: ticketData?.description || "",
      custom_fields: otherCf,
    };
  }, [isEditMode, ticketData]);

  const siteNameOptions = useMemo(
    () =>
      propertiesData?.res?.map(
        (property: { id: number; name: string }) => ({
          value: property?.name,
          content: (
            <span className="text-md text-heading-text dark:text-heading-text-dk">
              {property?.name}
            </span>
          ),
          selectedOptionContent: (
            <span className="text-md text-heading-text dark:text-heading-text-dk">
              {property?.name}
            </span>
          ),
        })
      ),
    [propertiesData]
  );

  const tagOptions = useMemo(
    () =>
      helpdeskTagsData?.map((tag: { tag_key: string; title: string }) => ({
        value: tag?.tag_key,
        label: tag?.title,
        content: (
          <span className="text-md text-heading-text dark:text-heading-text-dk">
            {tag?.title}
          </span>
        ),
      })) ?? [],
    [helpdeskTagsData]
  );

  const isUploadingAttachments = attachments?.some(
    (att) => att?.status === "uploading"
  );

  const customFieldDefinitions = useMemo(
    () => (customFieldsData ?? []) as HelpdeskCustomFieldDefinition[],
    [customFieldsData]
  );

  const KNOWN_FIELD_KEYS = useMemo(
    () => new Set(["system-name", "issue-type"]),
    []
  );

  const systemNameField = useMemo(
    () => customFieldDefinitions?.find((f) => f?.key === "system-name") ?? null,
    [customFieldDefinitions]
  );

  const issueTypeField = useMemo(
    () => customFieldDefinitions?.find((f) => f?.key === "issue-type") ?? null,
    [customFieldDefinitions]
  );

  const otherCustomFieldDefinitions = useMemo(
    () => customFieldDefinitions?.filter((f) => !KNOWN_FIELD_KEYS?.has(f?.key)),
    [customFieldDefinitions, KNOWN_FIELD_KEYS]
  );

  const orderedCustomFieldKeys = useMemo(
    () =>
      orderCustomFieldsForRender(otherCustomFieldDefinitions)?.map(
        (f) => f?.key
      ) ?? [],
    [otherCustomFieldDefinitions]
  );

  const editAttachmentItems: AttachmentItem[] = useMemo(() => {
    if (!isEditMode) return [];
    const apiAttachments = ticketAttachmentsData?.attachments ?? [];
    return apiAttachments
      .filter(
        (att: { s3_key: string }) => !removedAttachmentKeys.has(att.s3_key)
      )
      .map(
        (att: {
          s3_key: string;
          s3_url: string;
          file_name: string;
          content_type: string;
          size_in_bytes: number;
        }) => ({
          id: att.s3_key,
          file: null,
          name: att.file_name,
          size: att.size_in_bytes,
          mime: att.content_type,
          status: "sent" as const,
          remoteUrl: att.s3_key,
          s3Url: att.s3_url ?? "",
          previewUrl: att.content_type?.startsWith("image/")
            ? att.s3_url || att.s3_key
            : undefined,
        })
      );
  }, [isEditMode, ticketAttachmentsData, removedAttachmentKeys]);

  const hasAttachmentChanges = useMemo(() => {
    if (!isEditMode || initialAttachmentKeys === null) return false;
    const currentKeys = editAttachmentItems?.map((att) => att?.id);
    return !_.isEqual(_.sortBy(initialAttachmentKeys), _.sortBy(currentKeys));
  }, [isEditMode, initialAttachmentKeys, editAttachmentItems]);

  const validationSchema = useMemo(
    () =>
      buildTicketValidationSchema(
        otherCustomFieldDefinitions ?? [],
        region,
        issueTypeField
      ),
    [otherCustomFieldDefinitions, region, issueTypeField]
  );

  const baseHandleAttachmentsChange = useMemo(
    () =>
      createHandleAttachmentsChange(
        isUpdatingRef,
        attachmentsRef,
        setAttachments,
        uploadAttachment
      ),
    [uploadAttachment]
  );

  const handleAttachmentsChange = useCallback(
    (newAttachments: AttachmentItem[]) => {
      const isRemoval =
        newAttachments?.length < attachmentsRef?.current?.length;
      if (isRemoval) {
        setAttachments(newAttachments);
        attachmentsRef.current = newAttachments;
        isUpdatingRef.current = false;
        return;
      }

      const newFiles = newAttachments?.filter(
        (att) =>
          !attachmentsRef?.current?.some((existing) => existing?.id === att?.id)
      );
      const oversizedFiles = newFiles?.filter(
        (att) => att?.file && att?.file?.size > MAX_IMAGE_SIZE_BYTES
      );

      if (oversizedFiles?.length > 0) {
        customToast(
          `File size must not exceed 10MB. Please remove: ${oversizedFiles?.map((f) => f?.name)?.join(", ")}`,
          "error"
        );
        return;
      }

      baseHandleAttachmentsChange(newAttachments);
    },
    [baseHandleAttachmentsChange]
  );

  const handleAddTag = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery?.trim()) return;
      const title = searchQuery.trim();
      try {
        const result = await createHelpdeskTag({ title }).unwrap();
        const tagKey = result?.tag_key ?? title;
        if (!selectedTags.includes(tagKey)) {
          setSelectedTags((prev) => [...prev, tagKey]);
        }
        tagsRefetchRef.current?.abort();
        tagsRefetchRef.current = refetchHelpdeskTags();
      } catch {
        customToast("Failed to create tag", "error");
      }
    },
    [selectedTags, createHelpdeskTag, refetchHelpdeskTags]
  );

  const handleEditField = (field: HelpdeskCustomFieldDefinition) => {
    setCustomFieldModal({ open: true, fieldType: null, editingField: field });
  };

  const handleRequestDeleteField = (field: HelpdeskCustomFieldDefinition) => {
    setFieldPendingDelete(field);
  };

  const handleFormikSubmit = (
    values: CreateTicketFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    handleSubmitTicket(values, setSubmitting);
  };

  const handleAddCustomFieldMenuSelect = (fieldType: CustomFieldType) => {
    setCustomFieldModal({ open: true, fieldType, editingField: null });
  };

  const handleCustomFieldModalOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setCustomFieldModal({
        open: false,
        fieldType: null,
        editingField: null,
      });
    }
  };

  const handleCustomFieldSuccess = useCallback(() => {
    setTimeout(() => {
      formScrollRef.current?.scrollTo({
        top: formScrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 300);
  }, []);

  const handleGoBack = () => {
    if (isEditMode) {
      navigate(-1);
    } else {
      navigate("/dashboard/crm/helpdesk/all-tickets");
    }
  };

  const handleDescEditorReady = useCallback((editorInstance: Editor) => {
    setEditor(editorInstance);
  }, []);

  const scrollDescCursorIntoView = useCallback(() => {
    const ed = editorRef.current;
    const scrollContainer = editorScrollRef.current;
    if (!ed || !scrollContainer) return;

    requestAnimationFrame(() => {
      try {
        const from = ed?.state?.selection?.from;
        const coords = ed?.view?.coordsAtPos(from);
        const containerRect = scrollContainer?.getBoundingClientRect();
        const cursorBottom =
          coords?.bottom - containerRect?.top + scrollContainer?.scrollTop;
        const visibleBottom =
          scrollContainer?.scrollTop + scrollContainer?.clientHeight;

        if (cursorBottom > visibleBottom - 20) {
          scrollContainer.scrollTop =
            cursorBottom - scrollContainer?.clientHeight + 40;
        }
      } catch {
        console.error("Error scrolling cursor into view");
      }
    });
  }, []);

  const replaceDescImageSrc = useCallback(
    (ed: Editor, oldSrc: string, newSrc: string) => {
      if (ed?.isDestroyed) return;
      const { state } = ed;
      const { tr } = state;
      let modified = false;
      state?.doc?.descendants((node, pos) => {
        if (node?.type?.name === "image" && node?.attrs?.src === oldSrc) {
          tr?.setNodeMarkup(pos, undefined, { ...node?.attrs, src: newSrc });
          modified = true;
        }
      });
      if (modified) ed?.view?.dispatch(tr);
    },
    []
  );

  const insertDescImageFile = useCallback(
    (file: File, editorInstance?: Editor) => {
      setIsDescDragOver(false);
      if (file?.size > MAX_IMAGE_SIZE_BYTES) {
        customToast(
          `File "${file?.name}" exceeds 10MB limit and cannot be uploaded.`,
          "error"
        );
        return;
      }
      const ed = editorInstance || editorRef.current;
      if (!ed) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target?.result) return;
        const dataUrl = e.target.result as string;
        const img = new window.Image();
        img.src = dataUrl;
        img.onload = async () => {
          if (ed?.isDestroyed) return;
          const containerWidth = ed?.view?.dom?.clientWidth;
          const width =
            img?.naturalWidth > containerWidth
              ? "100%"
              : `${img?.naturalWidth}px`;
          ed.chain()
            .focus("end")
            .insertContent({ type: "image", attrs: { src: dataUrl, width } })
            .focus("end")
            .run();

          try {
            const res = await uploadAttachment({
              attachment: file,
              source: "conversation",
            })?.unwrap();
            const apiResponse = Array?.isArray(res) ? res?.[0] : res;
            if (apiResponse?.s3_url) {
              replaceDescImageSrc(ed, dataUrl, apiResponse?.s3_url);
            }
            if (isEditMode && ticketId) {
              createTicketAttachment({
                ticket_id: ticketId,
                s3_key: apiResponse?.s3_key,
                file_name: apiResponse?.file_name || file?.name,
                content_type: apiResponse?.content_type || file?.type,
                size_in_bytes: apiResponse?.size_in_bytes || file?.size,
                s3_url: apiResponse?.s3_url,
              });
            } else {
              const tempId = `desc_img_${Math?.random()?.toString(36)?.slice(2, 9)}`;
              setAttachments((prev) => [
                ...prev,
                {
                  id: tempId,
                  file,
                  name: apiResponse?.file_name || file?.name,
                  size: apiResponse?.size_in_bytes || file?.size,
                  mime: apiResponse?.content_type || file?.type,
                  previewUrl: apiResponse?.s3_url || dataUrl,
                  status: "sent" as const,
                  progress: 100,
                  remoteUrl: apiResponse?.s3_key ?? apiResponse?.s3_url ?? "",
                  s3Url: apiResponse?.s3_url ?? "",
                },
              ]);
            }
          } catch {
            console.error("Error inserting image file");
          }
        };
      };
      reader.readAsDataURL(file);
    },
    [
      uploadAttachment,
      replaceDescImageSrc,
      isEditMode,
      ticketId,
      createTicketAttachment,
    ]
  );

  const handleDescImageFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files?.length === 0) return;
      const imageFiles = Array.from(files).filter((f) =>
        f.type?.startsWith("image/")
      );
      const oversizedFiles = imageFiles?.filter(
        (f) => f?.size > MAX_IMAGE_SIZE_BYTES
      );

      if (oversizedFiles?.length > 0) {
        customToast(
          `File size must not exceed 10MB. Please select smaller images.`,
          "error"
        );
        if (imageDescInputRef.current) imageDescInputRef.current.value = "";
        return;
      }

      imageFiles?.forEach((f) => insertDescImageFile(f));
      if (imageDescInputRef.current) imageDescInputRef.current.value = "";
    },
    [insertDescImageFile]
  );

  const handleDescDragEnter = useCallback((e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
      setIsDescDragOver(true);
    }
  }, []);

  const handleDescDragOver = useCallback((e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
      setIsDescDragOver(true);
    }
  }, []);

  const handleDescDragLeave = useCallback((e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (descContainerRef.current?.contains(e.relatedTarget as Node)) return;
    setIsDescDragOver(false);
  }, []);

  const handleDescDrop = useCallback(
    (e: React.DragEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      setIsDescDragOver(false);
      const files = e.dataTransfer?.files;
      if (!files || files?.length === 0) return;
      const images = Array.from(files).filter((f) =>
        f.type?.startsWith("image/")
      );
      const oversizedFiles = images?.filter(
        (f) => f?.size > MAX_IMAGE_SIZE_BYTES
      );

      if (oversizedFiles?.length > 0) {
        customToast(
          `File size must not exceed 10MB. Please select smaller images.`,
          "error"
        );
        return;
      }

      images?.forEach((f) => insertDescImageFile(f));
    },
    [insertDescImageFile]
  );

  const handleEditAttachmentsChange = useCallback(
    (newItems: AttachmentItem[]) => {
      if (!ticketId) return;
      const newIds = new Set(newItems?.map((a) => a?.id));
      const removed = editAttachmentItems?.filter((a) => !newIds?.has(a?.id));
      if (removed?.length === 0) return;
      setRemovedAttachmentKeys((prev) => {
        const next = new Set(prev);
        removed?.forEach((att) => next?.add(att?.id));
        return next;
      });
    },
    [editAttachmentItems, ticketId]
  );

  const uploadAndRegisterAttachment = useCallback(
    (file: File) => {
      if (!ticketId) return;
      if (file?.size > MAX_IMAGE_SIZE_BYTES) {
        customToast(
          `File "${file?.name}" exceeds 10MB limit and cannot be uploaded.`,
          "error"
        );
        return;
      }
      uploadAttachment({ attachment: file, source: "conversation" })
        .unwrap()
        .then((res) => {
          const apiResponse = Array?.isArray(res) ? res?.[0] : res;
          if (apiResponse?.s3_url) {
            createTicketAttachment({
              ticket_id: ticketId,
              s3_key: apiResponse?.s3_key,
              file_name: apiResponse?.file_name || file?.name,
              content_type: apiResponse?.content_type || file?.type,
              size_in_bytes: apiResponse?.size_in_bytes || file?.size,
              s3_url: apiResponse?.s3_url,
            });
          }
        })
        .catch(() => customToast("Failed to upload attachment", "error"));
    },
    [ticketId, uploadAttachment, createTicketAttachment]
  );

  const handleEditAttachmentFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files?.length === 0) return;
      Array?.from(files)?.forEach(uploadAndRegisterAttachment);
      if (editAttachmentInputRef?.current)
        editAttachmentInputRef.current.value = "";
    },
    [uploadAndRegisterAttachment]
  );

  const handleEditAttachmentDrop = useCallback(
    (e: React.DragEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      const files = e?.dataTransfer?.files;
      if (!files || files?.length === 0) return;
      Array?.from(files)?.forEach(uploadAndRegisterAttachment);
    },
    [uploadAndRegisterAttachment]
  );

  const handleSubmitTicket = async (
    values: CreateTicketFormValues,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    try {
      if (!isEditMode && isUploadingAttachments) {
        customToast(
          "Attachments are still being uploaded. Please wait until all uploads complete.",
          "warning"
        );
        setSubmitting(false);
        return;
      }

      if (!isEditMode && attachments?.some((att) => att?.status === "failed")) {
        customToast(
          "Please remove failed attachments before submitting",
          "error"
        );
        setSubmitting(false);
        return;
      }

      const uploadedAttachments = !isEditMode
        ? attachments
            ?.filter((att) => att?.status === "sent" && att?.remoteUrl)
            ?.map((att) => ({
              s3_key: att?.remoteUrl ?? "",
              s3_url: att?.s3Url ?? "",
              file_name: att?.name,
              content_type: att?.mime || "",
              size_in_bytes: att?.size || 0,
            }))
        : [];

      const isFeatureRequest =
        values?.systemName === FEATURE_REQUESTS_SYSTEM_NAME;

      const priorityField: { priority: string | null } = isFeatureRequest
        ? { priority: null }
        : {
            priority:
              values?.priority !== undefined
                ? (PRIORITY_NUMBER_TO_STRING?.[values?.priority] ?? "low")
                : "low",
          };

      const mergedCustomFields: Record<string, unknown> = {
        ...values?.custom_fields,
      };
      if (values?.systemName) {
        mergedCustomFields["system-name"] = values?.systemName;
      }
      if (!isFeatureRequest && values?.issueType) {
        mergedCustomFields["issue-type"] = values?.issueType;
      }

      const serializedCustomFields: Record<
        string,
        string | number | boolean | string[] | null
      > = {
        ...serializeTicketCustomFieldsPayload(
          mergedCustomFields,
          customFieldDefinitions
        ),
      };

      if (isFeatureRequest) {
        serializedCustomFields["issue-type"] = null;
      }

      if (isEditMode && ticketId && ticketData) {
        const current = {
          ticket_category: values?.ticketCategory,
          title: values?.ticketSubject,
          property_name: values?.siteName,
          ...priorityField,
          description: values?.ticketDescription || "",
          tags: selectedTags,
          ...(Object?.keys(serializedCustomFields)?.length > 0
            ? { custom_fields: serializedCustomFields }
            : {}),
        };

        const original = {
          ticket_category: ticketData?.ticket_category || "Helpdesk Support",
          title: ticketData?.title,
          property_name: ticketData?.property_name,
          priority: ticketData?.priority ?? null,
          description: ticketData?.description || "",
          tags: ticketData?.tags ?? [],
          ...(Object?.keys(serializedCustomFields)?.length > 0
            ? { custom_fields: ticketData?.custom_fields ?? {} }
            : {}),
        };

        const changed = _.pickBy(
          current,
          (val, key) => !_.isEqual(val, original[key as keyof typeof original])
        );

        if (!_.isEmpty(changed)) {
          await updateTicket({
            ticketId,
            ...changed,
          })?.unwrap();
        }

        if (removedAttachmentKeys?.size > 0) {
          await deleteTicketAttachment({
            ticket_id: ticketId,
            s3_keys: Array.from(removedAttachmentKeys),
          })?.unwrap();
        }

        customToast(helpdeskLabels.ticketUpdatedSuccess, "success");
        navigate(-1);
        return;
      } else {
        const payload = {
          stage_key: "open",
          assignees: [],
          ticket_category: values?.ticketCategory,
          name: values?.fullName,
          email: values?.email,
          property_name: values?.siteName,
          title: values?.ticketSubject,
          ...(Object?.keys(serializedCustomFields)?.length > 0
            ? { custom_fields: serializedCustomFields }
            : {}),
          ...priorityField,
          description: values?.ticketDescription || "",
          attachments: uploadedAttachments,
          tags: selectedTags,
          request_support: requestSupport,
        };
        await createTicket(payload)?.unwrap();
        customToast(helpdeskLabels.ticketCreatedSuccess, "success");
      }
      navigate("/dashboard/crm/helpdesk/all-tickets");
    } catch {
      customToast(
        isEditMode ? helpdeskLabels.failedToUpdateTicket : helpdeskLabels.failedToCreateTicket,
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (
      !isEditMode ||
      !ticketAttachmentsData?.attachments ||
      initialAttachmentKeys !== null
    )
      return;
    setInitialAttachmentKeys(
      ticketAttachmentsData.attachments.map(
        (att: { s3_key: string }) => att.s3_key
      )
    );
  }, [isEditMode, ticketAttachmentsData, initialAttachmentKeys]);

  useEffect(() => {
    if (!isEditMode || !ticketData) return;
    setSelectedTags(ticketData?.tags ?? []);
    setRequestSupport(ticketData?.request_support ?? false);
  }, [isEditMode, ticketData]);

  useEffect(() => {
    if (
      !isEditMode ||
      !ticketData?.description ||
      !editor ||
      hasPrefilledEditor.current
    )
      return;
    editor?.commands?.setContent(ticketData?.description);
    hasPrefilledEditor.current = true;
  }, [isEditMode, ticketData?.description, editor]);

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    if (editInitialValues?.ticketCategory) {
      setSelectedTicketCategory(editInitialValues.ticketCategory);
    }
  }, [editInitialValues?.ticketCategory]);

  useEffect(() => {
    if (isEditMode) return;
    setAttachments([]);
    setSelectedTags([]);
    setRequestSupport(false);
    setRemovedAttachmentKeys(new Set());
    setInitialAttachmentKeys(null);
    setSelectedTicketCategory("sales");
    hasPrefilledEditor.current = false;
    editorRef.current?.commands?.clearContent();
  }, [isEditMode]);

  return (
    <div className="flex flex-col w-full h-full bg-pure-white dark:bg-pure-white-dk">
      <Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[8px]">
            <h1 className="text-xl font-semibold text-verba-indigo dark:text-verba-indigo-dk">
              Help Desk
            </h1>
            <hr className="border border-border-gray dark:border-border-gray-dk h-[16px] w-px" />
            <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
               {helpdeskLabels.ticketPlural}
            </p>
          </div>
        </div>
      </Header>

      <div
        className={`flex flex-col w-full h-full border-l border-t rounded-tl-[6px] border-border-gray dark:border-border-gray-dk overflow-auto ${visibleScrollbar(
          "[&::-webkit-scrollbar]:w-[8px]"
        )}`}
      >
        {isEditMode && isTicketLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <PulseLoader size={10} />
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between px-6 py-4 shrink-0">
              <div className="flex items-start gap-2.5">
                {showBackButton && (
                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="mt-0.5 p-1 rounded-md hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer"
                  >
                    <LeftArrowIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
                  </button>
                )}
                <div className="flex flex-col">
                  <h1 className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                    {isEditMode ? helpdeskLabels.editTicket : helpdeskLabels.createNewTicket}
                  </h1>
                  <p className="text-sm text-data dark:text-data-dk">
                    {isEditMode
                      ? helpdeskLabels.updateTicketDetails
                      : helpdeskLabels.createTicketFillDetails}
                  </p>
                </div>
              </div>
              <PermissionDisabledWrapper disabled={!canUpdate}>
                <AddCustomFieldMenu onSelect={handleAddCustomFieldMenuSelect} />
              </PermissionDisabledWrapper>
            </div>
            <div className="flex-1 flex min-h-0 justify-center overflow-hidden px-6">
              <div
                style={{
                  background: `
                    linear-gradient(90deg, rgba(40, 91, 213, 0.15) 0%, rgba(40, 91, 213, 0) 33%),
                    linear-gradient(90deg, rgba(198, 187, 255, 0) 20%, rgba(198, 187, 255, 0.08) 50%, rgba(198, 187, 255, 0) 80%),
                    linear-gradient(90deg, rgba(120, 223, 255, 0) 60%, rgba(120, 223, 255, 0.12) 85%, rgba(120, 223, 255, 0) 105%),
                    linear-gradient(180deg, rgba(255, 182, 182, 0.0434) 22.89%, rgba(97, 65, 225, 0) 100%)
                  `,
                }}
                className="rounded-[8px] border border-border-gray dark:border-border-gray-dk w-[55%] min-w-[480px] flex min-h-0 flex-col h-full max-h-full"
              >
                <Formik
                  key={ticketId ?? "create"}
                  initialValues={editInitialValues ?? initialValues}
                  validationSchema={validationSchema}
                  enableReinitialize={isEditMode}
                  onSubmit={handleFormikSubmit}
                >
                  {(formik) => {
                    const hasEditChanges = (() => {
                      if (!isEditMode || !editInitialValues || !ticketData)
                        return true;
                      if (hasAttachmentChanges) return true;
                      const v = formik?.values;
                      const iv = editInitialValues;
                      if (v?.ticketCategory !== iv?.ticketCategory) return true;
                      if (v?.ticketSubject !== iv?.ticketSubject) return true;
                      if (v?.siteName !== iv?.siteName) return true;
                      if (v?.ticketDescription !== iv?.ticketDescription)
                        return true;
                      if (
                        v?.systemName !== FEATURE_REQUESTS_SYSTEM_NAME &&
                        v?.priority !== iv?.priority
                      )
                        return true;
                      if (v?.systemName !== iv?.systemName) return true;
                      if (v?.issueType !== iv?.issueType) return true;
                      if (!_.isEqual(v?.custom_fields, iv?.custom_fields))
                        return true;
                      if (!_.isEqual(selectedTags, ticketData?.tags ?? []))
                        return true;
                      return false;
                    })();

                    const handleConfirmDeleteCustomField = async () => {
                      if (!fieldPendingDelete) return;
                      const key = fieldPendingDelete?.key;
                      try {
                        await deleteCustomField(key).unwrap();
                        customToast(
                          "Custom field deleted successfully",
                          "success"
                        );
                        formik?.setValues((prev) => ({
                          ...prev,
                          custom_fields: Object?.fromEntries(
                            Object?.entries(prev?.custom_fields)?.filter(
                              ([k]) => k !== key
                            )
                          ),
                        }));
                        setFieldPendingDelete(null);
                      } catch {
                        customToast("Failed to delete custom field", "error");
                      }
                    };

                    const handleTicketCategoryChange = (val: string) => {
                      formik?.setFieldValue("ticketCategory", val);
                      const directSystem = DIRECT_ISSUE_CATEGORY_MAP[val];
                      formik?.setFieldValue("systemName", directSystem ?? "");
                      formik?.setFieldValue("issueType", "");
                      setSelectedTicketCategory(val);
                    };

                    const handleSiteNameChange = (value: string) => {
                      formik?.setFieldValue("siteName", value);
                    };

                    const handlePriorityChange = (val: number) => {
                      if (formik?.values?.priority !== val && isEditMode) {
                        setPendingPriority(val);
                        setShowDueDateAlertModal(true);
                      } else {
                        formik?.setFieldValue("priority", val);
                      }
                    };

                    const handleConfirmPriority = () => {
                      if (pendingPriority !== null) {
                        formik?.setFieldValue("priority", pendingPriority);
                      }
                      setPendingPriority(null);
                      setShowDueDateAlertModal(false);
                    };

                    const handleCancelPriority = () => {
                      setPendingPriority(null);
                      setShowDueDateAlertModal(false);
                    };

                    const handleTicketDescriptionContainerBlur = (
                      e: React.FocusEvent<HTMLDivElement>
                    ) => {
                      if (
                        !e?.currentTarget?.contains(e?.relatedTarget as Node)
                      ) {
                        formik?.setFieldTouched("ticketDescription", true);
                      }
                    };

                    const handleTicketDescriptionMessageChange = (
                      html: string | undefined
                    ) => {
                      const normalized = isDescHtmlEmpty(html ?? "")
                        ? ""
                        : (html ?? "");
                      formik?.setFieldValue("ticketDescription", normalized);
                      scrollDescCursorIntoView();
                    };

                    const handleDescToolbarImageInsert = async (
                      file: File,
                      onUrlReplace: (url: string) => void
                    ) => {
                      try {
                        const res = await uploadAttachment({
                          attachment: file,
                          source: "conversation",
                        })?.unwrap();
                        const apiResponse = Array?.isArray(res)
                          ? res?.[0]
                          : res;
                        if (apiResponse?.s3_url) {
                          onUrlReplace(apiResponse?.s3_url);
                          if (isEditMode && ticketId) {
                            createTicketAttachment({
                              ticket_id: ticketId,
                              s3_key: apiResponse?.s3_key,
                              file_name: apiResponse?.file_name || file?.name,
                              content_type:
                                apiResponse?.content_type || file?.type,
                              size_in_bytes:
                                apiResponse?.size_in_bytes || file?.size,
                              s3_url: apiResponse?.s3_url,
                            });
                          } else {
                            const tempId = `desc_img_${Math?.random()?.toString(36)?.slice(2, 9)}`;
                            setAttachments((prev) => [
                              ...prev,
                              {
                                id: tempId,
                                file,
                                name: apiResponse?.file_name || file?.name,
                                size: apiResponse?.size_in_bytes || file?.size,
                                mime: apiResponse?.content_type || file?.type,
                                previewUrl: apiResponse?.s3_url,
                                status: "sent" as const,
                                progress: 100,
                                remoteUrl:
                                  apiResponse?.s3_key ??
                                  apiResponse?.s3_url ??
                                  "",
                                s3Url: apiResponse?.s3_url ?? "",
                              },
                            ]);
                          }
                        }
                      } catch {
                        console.error("Error inserting image file");
                      }
                    };

                    const handleTriggerAttachmentPicker = () => {
                      attachmentRef?.current?.triggerFilePicker();
                    };

                    const handleCustomFieldChange = (
                      key: string,
                      value: unknown
                    ) => {
                      formik?.setValues((prev) => ({
                        ...prev,
                        custom_fields: {
                          ...prev?.custom_fields,
                          [key]: value,
                        },
                      }));
                    };

                    const handlePrimarySubmitClick = () => {
                      if (!isEditMode && isUploadingAttachments) {
                        customToast(
                          "Attachments are still being uploaded. Please wait until all uploads complete.",
                          "warning"
                        );
                        return;
                      }
                      formik?.handleSubmit();
                    };

                    const handleDeleteModalOpenChange = (open: boolean) => {
                      if (!open) setFieldPendingDelete(null);
                    };

                    return (
                      <>
                        <ScrollToFirstError
                          containerRef={formScrollRef}
                          orderedCustomFieldKeys={orderedCustomFieldKeys}
                        />
                        <SiteNameAutoSelect
                          isEditMode={isEditMode}
                          options={siteNameOptions}
                          currentValue={formik?.values?.siteName ?? ""}
                          onAutoSelect={handleSiteNameChange}
                        />
                        <div
                          ref={formScrollRef}
                          className={`flex-1 min-h-0 overflow-auto ${visibleScrollbar(
                            "[&::-webkit-scrollbar]:w-[8px]"
                          )}`}
                        >
                          <form
                            id="create-ticket-form"
                            onSubmit={formik?.handleSubmit}
                            className="px-3.5 pt-6 pb-4 flex flex-col gap-4.5"
                          >
                            <TicketCategorySelector
                              value={formik?.values?.ticketCategory}
                              onChange={handleTicketCategoryChange}
                            />

                            <div className="grid grid-cols-2 gap-4">
                              <Input
                                label="Full name"
                                name="fullName"
                                id="fullName"
                                type="text"
                                placeholder="Enter your full name"
                                value={formik?.values?.fullName}
                                onChange={formik?.handleChange}
                                onBlur={formik?.handleBlur}
                                error={
                                  formik?.touched?.fullName &&
                                  Boolean(formik?.errors?.fullName)
                                }
                                errorMessage={
                                  formik?.touched?.fullName &&
                                  formik?.errors?.fullName
                                    ? formik?.errors?.fullName
                                    : undefined
                                }
                                secondaryContainerClassName="rounded-[8px] border border-border-gray dark:border-border-gray-dk focus-within:border-black dark:focus-within:border-white p-2 bg-pure-white dark:bg-pure-white-dk transition-colors"
                                labelClassName="text-md font-normal text-heading-text dark:text-heading-text-dk"
                              />
                              <Input
                                label="Email Id"
                                name="email"
                                id="email"
                                type="email"
                                placeholder="name@example.com"
                                value={formik?.values?.email}
                                onChange={formik?.handleChange}
                                onBlur={formik?.handleBlur}
                                error={
                                  formik?.touched?.email &&
                                  Boolean(formik?.errors?.email)
                                }
                                errorMessage={
                                  formik?.touched?.email &&
                                  formik?.errors?.email
                                    ? formik?.errors?.email
                                    : undefined
                                }
                                secondaryContainerClassName="rounded-[8px] border border-border-gray dark:border-border-gray-dk focus-within:border-black dark:focus-within:border-white p-2 bg-pure-white dark:bg-pure-white-dk transition-colors"
                                labelClassName="text-md font-normal text-heading-text dark:text-heading-text-dk"
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="flex flex-col gap-1">
                                <Dropdown
                                  id="site-name-dropdown"
                                  label="Site Name"
                                  options={siteNameOptions}
                                  value={formik?.values?.siteName}
                                  onChange={handleSiteNameChange}
                                  placeholder="Select site"
                                  ariaLabel="Select site"
                                  className="w-full"
                                  triggerClassName="!bg-pure-white dark:!bg-pure-white-dk"
                                  selectedOptionContainerClassName="h-[36px] w-full"
                                  labelClassName="text-md font-normal text-heading-text dark:text-heading-text-dk"
                                  placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
                                  dropdownClassName="!max-h-[150px]"
                                  optionClassName="border-b border-border-gray dark:border-border-gray-dk"
                                />
                                {formik?.touched?.siteName &&
                                  formik?.errors?.siteName && (
                                    <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
                                      {formik?.errors?.siteName}
                                    </span>
                                  )}
                              </div>
                            </div>

                            <Input
                              label={helpdeskLabels.ticketSubject}
                              name="ticketSubject"
                              id="ticketSubject"
                              type="text"
                              placeholder={`Enter ${helpdeskLabels.ticketSingular.toLowerCase()} subject`}
                              value={formik?.values?.ticketSubject}
                              onChange={formik?.handleChange}
                              onBlur={formik?.handleBlur}
                              error={
                                formik?.touched?.ticketSubject &&
                                Boolean(formik?.errors?.ticketSubject)
                              }
                              errorMessage={
                                formik?.touched?.ticketSubject &&
                                formik?.errors?.ticketSubject
                                  ? formik?.errors?.ticketSubject
                                  : undefined
                              }
                              secondaryContainerClassName="rounded-[8px] border border-border-gray dark:border-border-gray-dk focus-within:border-black dark:focus-within:border-white p-2 bg-pure-white dark:bg-pure-white-dk transition-colors"
                              labelClassName="text-md font-medium text-heading-text dark:text-heading-text-dk"
                            />

                            <SystemAndIssueTypeFields
                              formik={formik}
                              systemNameField={systemNameField}
                              issueTypeField={issueTypeField}
                              selectedTicketCategory={selectedTicketCategory}
                            />

                            {formik?.values?.systemName !==
                              FEATURE_REQUESTS_SYSTEM_NAME && (
                              <PrioritySelector
                                value={formik?.values?.priority}
                                onChange={handlePriorityChange}
                                error={
                                  formik?.touched?.priority &&
                                  formik?.errors?.priority
                                    ? formik?.errors?.priority
                                    : undefined
                                }
                              />
                            )}

                            <div
                              ref={descContainerRef}
                              className="flex flex-col gap-1 w-full"
                              data-form-field="ticketDescription"
                              onBlur={handleTicketDescriptionContainerBlur}
                              onDragEnter={handleDescDragEnter}
                              onDragOver={handleDescDragOver}
                              onDragLeave={handleDescDragLeave}
                              onDrop={handleDescDrop}
                            >
                              <label className="text-base font-medium text-heading-text dark:text-heading-text-dk mb-0.5">
                                {helpdeskLabels.ticketSingular} description
                              </label>
                              {isHelpdeskSupportCategory && (
                                <div className="text-sm text-verba-indigo dark:text-verba-indigo-dk mb-3.5">
                                  <p className="mb-1">
                                    Please be as concise as possible and include
                                    all relevant information including:
                                  </p>
                                  <ul className="flex flex-col gap-1 list-disc pl-[18px]">
                                    <li>Tenants/guarantors name</li>
                                    <li>Email address</li>
                                    <li>Room number</li>
                                    <li>Contract ID</li>
                                    <li>Amounts (where applicable)</li>
                                    <li>Room type or tenancy length</li>
                                  </ul>
                                </div>
                              )}
                              <div className="relative rounded-[8px] bg-pure-white dark:bg-pure-white-dk transition-colors flex flex-col min-h-[90px] max-h-[300px] border border-border-gray dark:border-border-gray-dk focus-within:border-black dark:focus-within:border-white">
                                {isDescDragOver && (
                                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[8px] bg-blue-50/60 dark:bg-blue-950/20 border-2 border-dashed border-blue-400/55 dark:border-blue-400/45">
                                    <div className="flex flex-col items-center gap-2">
                                      <ImageIcon className="h-8 w-8 text-blue-400 dark:text-blue-500" />
                                      <p className="text-sm font-medium text-blue-500 dark:text-blue-400">
                                        Drop images here
                                      </p>
                                    </div>
                                  </div>
                                )}
                                <div
                                  ref={editorScrollRef}
                                  className={`flex-1 min-h-0 overflow-y-auto px-2 py-1 ${visibleScrollbar(
                                    "[&::-webkit-scrollbar]:w-[6px]"
                                  )} ${isDescDragOver ? "pointer-events-none opacity-30" : ""}`}
                                >
                                  <RichTextEditor
                                    onMessageChange={
                                      handleTicketDescriptionMessageChange
                                    }
                                    onEditorReady={handleDescEditorReady}
                                    onImageFile={insertDescImageFile}
                                    placeholder={helpdeskLabels.ticketDescriptionPlaceholder}
                                  />
                                </div>
                              </div>
                              {editor && (
                                <div className="px-2 py-1 shrink-0 flex items-center gap-3 overflow-hidden">
                                  <div
                                    className={`flex-1 min-w-0 overflow-x-auto dark:[&_button>svg]:text-verba-indigo-dk! dark:[&_.truncate]:text-verba-indigo-dk! [&_button]:font-normal! ${visibleScrollbar("[&::-webkit-scrollbar]:h-[4px]")}`}
                                  >
                                    <RichTextEditorToolbar
                                      editor={editor}
                                      schema={TICKET_DESCRIPTION_TOOLBAR_SCHEMA}
                                      toolbarExtraClass="w-full max-w-none !shadow-none"
                                      onImageInsert={
                                        handleDescToolbarImageInsert
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                              {formik?.touched?.ticketDescription &&
                                formik?.errors?.ticketDescription && (
                                  <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
                                    {formik?.errors?.ticketDescription}
                                  </span>
                                )}
                            </div>
                            <input
                              ref={imageDescInputRef}
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleDescImageFileSelect}
                              className="hidden"
                            />

                            {isEditMode ? (
                              <div className="flex flex-col gap-[6px]">
                                <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                                  Attachments{" "}
                                  <span className="text-gray-placeholder dark:text-gray-placeholder-dk text-xs">
                                    (Optional)
                                  </span>
                                </p>
                                <button
                                  type="button"
                                  onClick={() =>
                                    editAttachmentInputRef?.current?.click()
                                  }
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onDrop={handleEditAttachmentDrop}
                                  className="w-full rounded-[8px] border border-dashed border-verba-blue dark:border-verba-blue-dk bg-applied-filters-tag-bg/25 dark:bg-applied-filters-tag-bg-dk/25 flex items-center justify-center gap-2 h-[80px] cursor-pointer hover:bg-applied-filters-tag-bg/50 dark:hover:bg-applied-filters-tag-bg-dk/50 transition-colors"
                                >
                                  <UploadLeadsIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk shrink-0" />
                                  <div className="flex flex-col items-start">
                                    <span className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk leading-normal">
                                      Click to upload or drag and drop
                                    </span>
                                    <span className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
                                      {TICKET_ATTACHMENT_FORMATS_LABEL}
                                    </span>
                                  </div>
                                </button>
                                <input
                                  ref={editAttachmentInputRef}
                                  type="file"
                                  accept={TICKET_ATTACHMENT_ACCEPT}
                                  multiple
                                  onChange={handleEditAttachmentFileSelect}
                                  className="hidden"
                                />
                                {editAttachmentItems?.length > 0 && (
                                  <div className="mt-2">
                                    <AttachmentInput
                                      value={editAttachmentItems}
                                      onChange={handleEditAttachmentsChange}
                                      variant="compact"
                                      maxFiles={20}
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div
                                className={`flex flex-col gap-[6px] ${
                                  isUploadingAttachments
                                    ? "pointer-events-none opacity-50 cursor-not-allowed"
                                    : ""
                                }`}
                              >
                                <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                                  Attachments{" "}
                                  <span className="text-gray-placeholder dark:text-gray-placeholder-dk text-xs">
                                    (Optional)
                                  </span>
                                </p>
                                <button
                                  type="button"
                                  onClick={handleTriggerAttachmentPicker}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    attachmentRef?.current?.addFiles(
                                      e?.dataTransfer?.files
                                    );
                                  }}
                                  className="w-full rounded-[8px] border border-dashed border-verba-blue dark:border-verba-blue-dk bg-applied-filters-tag-bg/25 dark:bg-applied-filters-tag-bg-dk/25 flex items-center justify-center gap-2 h-[80px] cursor-pointer hover:bg-applied-filters-tag-bg/50 dark:hover:bg-applied-filters-tag-bg-dk/50 transition-colors"
                                >
                                  <UploadLeadsIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk shrink-0" />
                                  <div className="flex flex-col items-start">
                                    <span className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk leading-normal">
                                      Click to upload or drag and drop
                                    </span>
                                    <span className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
                                      {TICKET_ATTACHMENT_FORMATS_LABEL}
                                    </span>
                                  </div>
                                </button>
                                <div className="mt-2">
                                  <AttachmentInput
                                    ref={attachmentRef}
                                    value={attachments}
                                    onChange={handleAttachmentsChange}
                                    variant="compact"
                                    maxFiles={20}
                                    accept={TICKET_ATTACHMENT_ACCEPT}
                                    allowExcel
                                  />
                                </div>
                              </div>
                            )}

                            {isEditMode && (
                              <MultiSelectSearchableDropdown
                                id="ticket-tags-multiselect"
                                label="Tags"
                                labelClassName="text-md font-normal text-heading-text dark:text-heading-text-dk"
                                options={tagOptions}
                                value={selectedTags}
                                onChange={setSelectedTags}
                                placeholder="Select tags"
                                searchPlaceholder="Search tags..."
                                autoFitChips
                                showSearch={true}
                                className="w-full"
                                placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
                                triggerClassName="min-h-[36px] rounded-[8px] !bg-pure-white dark:!bg-pure-white-dk"
                                dropdownOpenClassName="!max-h-[150px] mt-1"
                                chipClassName="inline-flex items-center gap-1 px-2 py-0.5 min-h-[19px] text-xs rounded-[4px] border border-border-gray dark:border-border-gray-dk bg-hover-effect dark:bg-hover-effect-dk text-verba-indigo dark:text-verba-indigo-dk"
                                moreTagClassName="inline-flex items-center px-2 py-[3px] text-xs bg-card-highlight dark:bg-card-highlight-dk rounded-[4px] text-heading-text dark:text-heading-text-dk shrink-0"
                                addButtonIcon={AddIcon}
                                addButtonLabel={(query) =>
                                  query?.trim()
                                    ? `Add '${query.trim()}' tag`
                                    : "Add new tag"
                                }
                                addButtonIconClassName="w-4 h-4 text-verba-blue dark:text-verba-blue-dk"
                                addButtonClassName="text-verba-blue dark:text-verba-blue-dk"
                                addButtonLabelClassName="text-md font-semibold"
                                addButtonLoading={
                                  isLoadingCreateTagMutation ||
                                  isFetchingHelpdeskTags
                                }
                                addButtonLoadingText="Adding tag..."
                                onAddButtonClick={(searchQuery) =>
                                  handleAddTag(searchQuery)
                                }
                                disabled={!canUpdate}
                              />
                            )}

                            <RequestSupportSelector
                              checked={requestSupport}
                              onChange={setRequestSupport}
                            />

                            {otherCustomFieldDefinitions &&
                              otherCustomFieldDefinitions?.length > 0 && (
                                <>
                                  <hr className="h-px border-0 bg-border-gray dark:bg-border-gray-dk" />
                                  <CreateTicketDynamicFields
                                    fieldDefinitions={
                                      otherCustomFieldDefinitions
                                    }
                                    values={formik?.values?.custom_fields}
                                    onFieldChange={handleCustomFieldChange}
                                    errors={
                                      typeof formik?.errors?.custom_fields ===
                                      "object"
                                        ? (formik?.errors
                                            ?.custom_fields as Record<
                                            string,
                                            string
                                          >)
                                        : undefined
                                    }
                                    showErrors={formik?.submitCount > 0}
                                    onEditField={handleEditField}
                                    onDeleteField={handleRequestDeleteField}
                                    actionsDisabled={!canUpdate}
                                  />
                                </>
                              )}
                          </form>
                        </div>

                        <div className="shrink-0 flex items-center justify-between px-8 py-4 border-t border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk">
                          <Button
                            type="button"
                            innerText="Cancel & Go Back"
                            onClick={handleGoBack}
                            buttonContainerClass="min-w-[150px] py-1.5 rounded-md border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                            innerTextClass="text-md font-semibold text-heading-text dark:text-heading-text-dk"
                          />
                          <Button
                            type="submit"
                            innerText={
                              isEditMode ? `Update ${helpdeskLabels.ticketSingular}` : helpdeskLabels.createTicket
                            }
                            onClick={handlePrimarySubmitClick}
                            buttonContainerClass="min-w-[150px] py-1.5 rounded-md bg-verba-indigo dark:bg-verba-indigo-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-verba-indigo dark:disabled:hover:bg-verba-indigo-dk disabled:dark:hover:text-verba-indigo-dk disabled:!cursor-not-allowed"
                            innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
                            isLoading={formik?.isSubmitting}
                            disabled={
                              formik?.isSubmitting ||
                              (!isEditMode && isUploadingAttachments) ||
                              (isEditMode && !hasEditChanges)
                            }
                          />
                        </div>

                        <DeleteHelpdeskCustomFieldModal
                          open={Boolean(fieldPendingDelete)}
                          onOpenChange={handleDeleteModalOpenChange}
                          onConfirm={handleConfirmDeleteCustomField}
                          isDeleting={isDeletingCustomField}
                        />

                        <DueDateAlertModal
                          open={showDueDateAlertModal}
                          onOpenChange={handleCancelPriority}
                          onSubmit={handleConfirmPriority}
                        />
                      </>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </>
        )}
      </div>

      <HelpdeskTicketCustomFieldModal
        open={customFieldModal?.open}
        fieldType={customFieldModal?.fieldType}
        editingField={customFieldModal?.editingField}
        onOpenChange={handleCustomFieldModalOpenChange}
        onSuccess={handleCustomFieldSuccess}
      />
    </div>
  );
};
